import React, { FC, useEffect, useState } from 'react';

import {
  Stack,
  HStack,
  Box,
  useDisclosure,
  Text,
  Center,
} from '@chakra-ui/react';
import { forEach, isEmpty, map, slice, toArray } from 'lodash';

import {
  DetailsIdentifier,
  ExpandableText,
  formatTooltip,
  OrderedList,
  PlusCountTag,
} from 'components/DataDisplay';
import { VulnerabilityIcon } from 'components/Icons';
import { CVEDetailsDrawer } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/CVE/CVEDetailsDrawer';
import { renderCVEInsights } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/utils';

import { customTheme } from '../../../../../../../theme';

interface Props {
  row: Record<string, any>;
}

export const PackagesHostsDrawerDetails: FC<Props> = props => {
  const { row } = props;
  const [currentCve, setCurrentCve] = useState('');
  const cveDetailsDisclosure = useDisclosure();
  const [cves, setCves] = useState<string[]>([]);

  useEffect(() => {
    const items = new Set();
    forEach(row?.cve_ids, o => items.add(o));
    setCves(toArray(items) as string[]);
  }, [row]);

  return (
    <>
      <HStack align="flex-start" spacing={10}>
        <Stack w="70%" spacing={3}>
          <DetailsIdentifier
            label="Description"
            value={
              row?.description ? (
                <ExpandableText noOfLines={2}>
                  {row?.description}
                </ExpandableText>
              ) : (
                '-'
              )
            }
            isInline={false}
          />
          <HStack w="full">
            <Box w="full">
              <DetailsIdentifier
                label="Package"
                value={row?.package_name}
                isInline={false}
              />
            </Box>
            <Box w="full">
              <DetailsIdentifier
                label="Version"
                value={row?.installed_version}
                isInline={false}
              />
            </Box>
            <Box w="full">
              <DetailsIdentifier
                label="Patch Version(s)"
                value={
                  <Text noOfLines={1}>
                    {!isEmpty(row?.patch_versions)
                      ? row?.patch_versions?.join(', ')
                      : '-'}
                  </Text>
                }
                isInline={false}
                tooltip={
                  !isEmpty(row?.patch_versions)
                    ? formatTooltip({
                        header: 'Patch Versions',
                        content: <OrderedList list={row?.patch_versions} />,
                        width: 'full',
                      })
                    : undefined
                }
              />
            </Box>
          </HStack>
        </Stack>
        <Stack w="40%" spacing={3}>
          <DetailsIdentifier
            label="CVE ID"
            value={
              <HStack>
                <Box boxSize={5} color="critical">
                  <Center>
                    <VulnerabilityIcon />
                  </Center>
                </Box>
                {!cves?.length && (
                  <Box color={customTheme.colors.gray['250']}>None</Box>
                )}
                {cves?.[0] && (
                  <HStack>
                    <Text
                      textDecoration="underline"
                      cursor="pointer"
                      onClick={() => {
                        cveDetailsDisclosure.onOpen();
                        setCurrentCve(cves?.[0]);
                      }}
                    >
                      {cves?.[0]}
                    </Text>
                  </HStack>
                )}
                {cves?.length > 1 && (
                  <PlusCountTag
                    count={cves.length - 1}
                    tooltip={formatTooltip({
                      header: 'CVEs',
                      content: (
                        <Stack spacing={1}>
                          {map(slice(cves, 1, cves.length), (o, index) => (
                            <HStack>
                              <Box>{index + 1}.</Box>
                              <Box>{o}</Box>
                            </HStack>
                          ))}
                        </Stack>
                      ),
                      width: 40,
                    })}
                    tooltipStyles={{ tooltip: { placement: 'right-start' } }}
                  />
                )}
              </HStack>
            }
            isInline={false}
          />
          <DetailsIdentifier
            label="Insights"
            value={renderCVEInsights(row)}
            isInline={false}
          />
        </Stack>
        {cveDetailsDisclosure.isOpen && (
          <CVEDetailsDrawer
            isOpen={cveDetailsDisclosure.isOpen}
            onClose={cveDetailsDisclosure.onClose}
            cveId={currentCve}
          />
        )}
      </HStack>
    </>
  );
};
